import React from 'react';

import { PlacedAnnotationSchema } from '@plangrid/scribble-client';
import styled from 'styled-components';

import A from '@adsk/alloy-react-a';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import UnstyledAnchor from '../../../UnstyledAnchor';
import { dateToLocalString } from '../helpers/utils';

const Container = styled(UnstyledAnchor)`
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.charcoal100};
  cursor: pointer;
  display: flex;
  height: 90px;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex: 1;
  overflow: hidden;

  :last-child {
    border-bottom: none;
  }
`;

const ReferenceLink = styled.div`
  font-size: ${theme.typography.bodyMedium.fontSize};
  line-height: ${theme.typography.bodyMedium.lineHeight};
  a {
    text-decoration: none;
    display: inline-flex;
    span {
      margin-top: 2px;
      margin-left: 8px;
    }
  }
`;

const NullReferenceLink = styled(ReferenceLink)`
  line-height: ${theme.typography.bodyMedium.lineHeight};
  a {
    color: ${theme.colors.charcoal700};
  }
`;

const MetadataContainer = styled.div`
  flex: 1;
  width: 80%;
`;

const PipeDetailContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const BoldTitle = styled.span`
  font-weight: bold;
`;

const Divider = styled.div`
  padding: 0px 12px;
  color: ${theme.colors.charcoal400};
`;

type MarkupsEntityBlockViewProps = {
  name: string;
  url: string;
  sourceEntityType?: string;
  markups: PlacedAnnotationSchema[];
  isLoading: boolean;
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

const MarkupsEntityBlockView = ({
  name,
  url,
  sourceEntityType,
  markups,
  isLoading,
  onClick,
}: MarkupsEntityBlockViewProps) => {
  const { T, locale } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`),
  );
  const countText = (count: number) => T.translate('MARKUP_TEXT', { count });
  const loadingText = () => T.translate('LOAD_MARKUPS');
  const noMarkupText = () =>
    T.translate('NO_MARKUPS', {
      type: (sourceEntityType || '').toUpperCase(),
      name,
    });

  const emptyView = () => (
    <Container>
      <Tooltip
        content={T.translate('NO_MARKUPS')}
        trigger={Tooltip.TRIGGER_EVENTS.CLICK}
        placement={Tooltip.PLACEMENTS.BOTTOM}
      >
        <NullReferenceLink>
          <A href={url}>
            <AlertWarningIcon />
            <span>{isLoading ? loadingText() : noMarkupText()}</span>
          </A>
        </NullReferenceLink>
      </Tooltip>
    </Container>
  );

  const pickerView = () => (
    <Container onClick={onClick} href={url}>
      <MetadataContainer>
        <OverflowTooltip
          getTargetProps={() => ({
            style: { overflow: 'hidden', flex: 'auto' },
          })}
          content={name}
        >
          <BoldTitle>{name}</BoldTitle>
        </OverflowTooltip>
        <PipeDetailContainer>
          <OverflowTooltip
            getTargetProps={() => ({
              style: {
                overflow: 'hidden',
                maxWidth: '50%',
                color: theme.colors.charcoal900,
              },
            })}
            content={markups[0].updated_at}
          >
            {dateToLocalString(markups[0].updated_at, locale)}
          </OverflowTooltip>
          <Divider>|</Divider>
          <OverflowTooltip
            getTargetProps={() => ({
              style: {
                overflow: 'hidden',
                flex: 1,
                color: theme.colors.charcoal900,
              },
            })}
            content={`${countText(markups.length)}`}
          >
            {`${countText(markups.length)}`}
          </OverflowTooltip>
        </PipeDetailContainer>
      </MetadataContainer>
    </Container>
  );

  return (
    <React.Fragment>
      {!isLoading && markups.length > 0 ? pickerView() : emptyView()}
    </React.Fragment>
  );
};

export default MarkupsEntityBlockView;
